import axios from 'axios';
import store from '../../store/store';

const baseURL = process.env.REACT_APP_API_URL || '';

const ApiService = axios.create({
  baseURL: baseURL,
  timeout: 5000,
});

ApiService.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

ApiService.checkTokenExpiration = async () => {
  const state = store.getState();
  const {
    refreshToken,
    accessTokenExpiration,
    refreshTokenExpiration,
  } = state.tokens;

  const currentTime = Date.now();
  const accessTokenExpiresAtMs = accessTokenExpiration
    ? new Date(accessTokenExpiration).getTime()
    : 0;
  const refreshTokenExpiresAtMs = refreshTokenExpiration
    ? new Date(refreshTokenExpiration).getTime()
    : 0;

  if (accessTokenExpiresAtMs && accessTokenExpiresAtMs < currentTime) {
    console.warn('Access token expired.');

    if (refreshTokenExpiresAtMs && refreshTokenExpiresAtMs > currentTime) {
      try {
        await ApiService.refreshTokens(refreshToken);
        return true;
      } catch (error) {
        console.error('Error refreshing token:', error);
        alert('Sesja wygasła. Zaloguj się ponownie.');
        store.dispatch({ type: 'tokens/clearTokens' });
        window.location.href = '/';
        return false;
      }
    } else {
      console.warn('Refresh token expired. Logging out...');
      alert('Sesja wygasła. Zaloguj się ponownie.');
      store.dispatch({ type: 'tokens/clearTokens' });
      window.location.href = '/';
      return false;
    }
  }

  return true;
};

ApiService.refreshTokens = async (refreshToken) => {
  const response = await ApiService.get('/api/auth/access-token', {
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
  });

  const { token: accessToken, expiresAt: accessTokenExpiresAt } = response.data;

  store.dispatch({
    type: 'tokens/setAccessTokenWithExpiration',
    payload: { accessToken, accessTokenExpiresAt },
  });

  return { accessToken, accessTokenExpiresAt };
};

ApiService.getHeadersJsonUTF8OrDifferent = (
  contentType = 'application/json; charset=UTF-8'
) => {
  const token = store.getState().tokens.accessToken;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': contentType,
  };
};

ApiService.getHeaderOnlyToken = () => {
  const token = store.getState().tokens.accessToken;
  return {
    Authorization: `Bearer ${token}`,
  };
};

ApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 400:
          console.error('400: Bad request', data);
          alert('Nieprawidłowe dane.');
          break;
        case 401:
          console.error('401: Unauthorized', data);
          alert('Sesja wygasła. Zaloguj się ponownie.');
          store.dispatch({ type: 'tokens/clearTokens' });
          window.location.href = '/';
          break;
        case 403:
          console.error('403: Forbidden', data);
          alert('Brak uprawnień do tej operacji.');
          break;
        case 404:
          console.error('404: Not found', data);
          alert('Nie znaleziono zasobu.');
          break;
        case 500:
          console.error('500: Server error', data);
          alert('Błąd serwera. Spróbuj ponownie później.');
          break;
        default:
          console.error(`Error ${status}:`, data);
          alert('Wystąpił błąd. Spróbuj ponownie.');
      }
    } else if (error.request) {
      console.error('No response from server:', error.request);
      alert('Brak połączenia z serwerem. Sprawdź połączenie.');
    } else {
      console.error('Error:', error.message);
      alert('Wystąpił błąd. Spróbuj ponownie.');
    }
    return Promise.reject(error);
  }
);

ApiService.login = async (login, pass) => {
  const response = await ApiService.post('/api/auth/login', { login, pass });
  return response.data;
};

/**
 * Ring volume and weight – POST /api/ring/volume
 * @param {{ roz: number, szer: number, height: number, r1: number, r2: number, densityGPerCm3?: number, korektaWagi?: number, palladGold?: boolean, bialyMm?: number, sumaKMm?: number, baseMaterialDensityGPerCm3?: number }} body
 * @returns {Promise<{ volumeCm3: number, weightGrams: number, checkpointPalladGold?: boolean, pdX?: number | null }>}
 */
ApiService.ringVolume = async (body) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post('/api/ring/volume', body, {
    headers: {
      ...ApiService.getHeaderOnlyToken(),
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

/**
 * Ring profile radii R1, R2 – POST /api/ring/radii
 * @param {{ r1: number, r2: number, nominalHeightWys: number, actualHeightWys: number, lensCf: boolean, actualWidthSzer: number, nominalWidthWSzer: number, skosyBevelHeight: number }} body
 * @returns {Promise<{ r1: number, r2: number, error: string | null }>}
 */
ApiService.ringRadii = async (body) => {
  await ApiService.checkTokenExpiration();
  const response = await ApiService.post('/api/ring/radii', body, {
    headers: {
      ...ApiService.getHeaderOnlyToken(),
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export default ApiService;
